import React, { useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { getLayerFromSlug, getLayerSlug } from '../../utils/routeUtils';
import { getFeatureShareKey } from '../../utils/shareUtils';
import { useMapStore } from '../../store/useMapStore';
import type { ServiceLayer, GisFeature } from '../../types/gis';
import { ENABLE_LAND_PARCEL } from '../../config/features';

export const RouteManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { layer, district } = useParams();
  
  const activeLayer = useMapStore(state => state.activeLayer);
  const setActiveLayer = useMapStore(state => state.setActiveLayer);
  const activeDistrict = useMapStore(state => state.activeDistrict);
  const setActiveDistrict = useMapStore(state => state.setActiveDistrict);
  const setPendingPincodeResolve = useMapStore(state => state.setPendingPincodeResolve);
  const setPendingVeterinaryResolve = useMapStore(state => state.setPendingVeterinaryResolve);
  const searchResult = useMapStore(state => state.searchResult);
  const selectedVeterinaryFacility = useMapStore(state => state.selectedVeterinaryFacility);
  const selectedRegistrationOffice = useMapStore(state => state.selectedRegistrationOffice);
  const pendingRegistrationResolve = useMapStore(state => state.pendingRegistrationResolve);
  const setPendingRegistrationResolve = useMapStore(state => state.setPendingRegistrationResolve);
  const selectedLandParcel = useMapStore(state => state.selectedLandParcel);
  const setPendingLandParcelResolve = useMapStore(state => state.setPendingLandParcelResolve);
  const setAutoLocateAttempted = useMapStore(state => state.setAutoLocateAttempted);
  const selectedTemple = useMapStore(state => state.selectedTemple);
  const setPendingTempleResolve = useMapStore(state => state.setPendingTempleResolve);

  const isSyncingRef = React.useRef(false);
  const lastHandledPincodeRef = React.useRef<string | null>(null);
  const lastPathRef = React.useRef('');

  // Sync URL -> Store
  useEffect(() => {
    const currentPath = `${layer || ''}/${district || ''}`;
    if (lastPathRef.current === currentPath) {
      return;
    }
    lastPathRef.current = currentPath;

    isSyncingRef.current = true;
    if (layer) {
      const resolvedLayer = getLayerFromSlug(layer);
      const validLayers: ServiceLayer[] = ['PINCODE', 'PDS', 'TNEB', 'CONSTITUENCY', 'POLICE', 'HEALTH', 'LOCAL_BODIES_V2', 'VETERINARY', 'REGISTRATION', 'LAND_PARCEL', 'TEMPLES', 'RADIO', 'FUEL'];
      if (resolvedLayer && validLayers.includes(resolvedLayer) && resolvedLayer !== activeLayer) {
        if (resolvedLayer === 'LAND_PARCEL' && !ENABLE_LAND_PARCEL) return;
        setActiveLayer(resolvedLayer);
      }
    }

    if (district) {
      const isPincode = /^\d{6}$/.test(district);
      if (isPincode) {
        const currentPin = (
          searchResult?.properties?.pin_code ||
          searchResult?.properties?.PIN_CODE ||
          searchResult?.properties?.pincode
        )?.toString() || null;

        // Prevent re-trigger loops when the URL pin has already been handled
        // and state changes (e.g. PDS district load) cause this effect to rerun.
        if (lastHandledPincodeRef.current !== district && currentPin !== district) {
          setPendingPincodeResolve(district);
          setAutoLocateAttempted(true);
          lastHandledPincodeRef.current = district;
        }
        if (activeDistrict) setActiveDistrict(null);
      } else if (layer === 'veterinary' && district.startsWith('vet_')) {
        // Handle direct deep-link to specific veterinary facility
        const currentVetKey = selectedVeterinaryFacility ? getFeatureShareKey(selectedVeterinaryFacility, 'VETERINARY') : null;
        if (currentVetKey !== district) {
          setPendingVeterinaryResolve(district);
          setAutoLocateAttempted(true);
        }
        if (activeDistrict) setActiveDistrict(null);
      } else if (layer === 'registration' && district.startsWith('sro_')) {
        // Handle direct deep-link to specific registration office
        if (pendingRegistrationResolve !== district) {
          setPendingRegistrationResolve(district);
          setAutoLocateAttempted(true);
        }
        if (activeDistrict) setActiveDistrict(null);
      } else if (ENABLE_LAND_PARCEL && layer === 'land-parcel' && district.includes(',')) {
        // Handle coordinate deep-link for land parcel
        const currentParcelKey = selectedLandParcel ? getFeatureShareKey(selectedLandParcel, 'LAND_PARCEL') : null;
        if (currentParcelKey !== district) {
          setPendingLandParcelResolve(district);
          setAutoLocateAttempted(true);
        }
        if (activeDistrict) setActiveDistrict(null);
      } else if (layer === 'temples' && district.startsWith('temple_')) {
        // Handle direct deep-link to specific temple
        const currentTempleKey = selectedTemple ? getFeatureShareKey(selectedTemple as unknown as GisFeature, 'TEMPLES') : null;
        if (currentTempleKey !== district) {
          setPendingTempleResolve(district);
          setAutoLocateAttempted(true);
        }
        if (activeDistrict) setActiveDistrict(null);
      } else if (district !== activeDistrict) {
        lastHandledPincodeRef.current = null;
        const decodedDistrict = decodeURIComponent(district);
        setActiveDistrict(decodedDistrict);
      }
    } else if (!district && activeDistrict) {
      lastHandledPincodeRef.current = null;
      // Clear district if navigating to a base layer URL
      setActiveDistrict(null);
    }
    
    // Reset the flag after a short delay or in next tick to allow store updates to settle
    setTimeout(() => { isSyncingRef.current = false; }, 0);
  }, [layer, district, activeLayer, activeDistrict, searchResult, selectedVeterinaryFacility, selectedTemple, setActiveLayer, setActiveDistrict, setPendingPincodeResolve, setPendingVeterinaryResolve, setPendingTempleResolve, setAutoLocateAttempted]);

  // Sync Store -> URL (for internal state changes not triggered by URL)
  useEffect(() => {
    if (isSyncingRef.current) return;

    const currentLayerPath = getLayerSlug(activeLayer);
    
    // Priority: Layer-specific selected feature -> Current search result's pincode (for precision shareability) -> activeDistrict (fallback fallback)
    let subPath = '';
    const resultPin = (searchResult?.properties?.pin_code || searchResult?.properties?.PIN_CODE || searchResult?.properties?.pincode)?.toString();
    
    if (activeLayer === 'TEMPLES' && selectedTemple) {
      const templeKey = getFeatureShareKey(selectedTemple as unknown as GisFeature, 'TEMPLES');
      if (templeKey) subPath = `/${encodeURIComponent(templeKey)}`;
    } else if (activeLayer === 'VETERINARY' && selectedVeterinaryFacility) {
      const vetKey = getFeatureShareKey(selectedVeterinaryFacility, 'VETERINARY');
      if (vetKey) subPath = `/${encodeURIComponent(vetKey)}`;
    } else if (activeLayer === 'REGISTRATION' && selectedRegistrationOffice) {
      const sroKey = getFeatureShareKey(selectedRegistrationOffice, 'REGISTRATION');
      if (sroKey) subPath = `/${encodeURIComponent(sroKey)}`;
    } else if (ENABLE_LAND_PARCEL && activeLayer === 'LAND_PARCEL' && selectedLandParcel) {
      const parcelKey = getFeatureShareKey(selectedLandParcel, 'LAND_PARCEL');
      if (parcelKey) subPath = `/${parcelKey}`; // No encode needed for lat,lng
    } else if (resultPin && /^\d{6}$/.test(resultPin)) {
      subPath = `/${resultPin}`;
    } else if (activeDistrict) {
      subPath = `/${encodeURIComponent(activeDistrict)}`;
    }

    const newPath = `/${currentLayerPath}${subPath}`;

    const normalizedCurrentPath = location.pathname.replace(/\/$/, '');
    const normalizedNewPath = newPath.replace(/\/$/, '');

    if (normalizedCurrentPath !== normalizedNewPath) {
       navigate(newPath, { replace: true });
    }
  }, [activeLayer, activeDistrict, searchResult, selectedVeterinaryFacility, selectedTemple, location.pathname, navigate]);

  return null;
};
